/**
 * Barre de récompenses : échange 50 ml vers 100 ml
 * =============================================================================
 * Seul JS de la fonctionnalité. Tout l'affichage (jauge, récapitulatif, carte)
 * est calculé en Liquid dans snippets/cart-rewards.liquid : cart-drawer.js
 * remplace l'intégralité de .drawer__inner après chaque mutation du panier,
 * donc l'état est recalculé côté serveur et n'a rien à resynchroniser ici.
 *
 * Trois invariants, dans l'ordre d'importance :
 *
 * 1. ON AJOUTE AVANT DE RETIRER. L'ordre inverse ferait passer le panier par
 *    l'état vide et le drawer basculerait sur sa branche "panier vide".
 *
 * 2. ON RETIRE PAR CLÉ DE LIGNE, jamais par index. L'ajout qu'on vient de faire
 *    décale les index, et free-gift.js peut insérer la ligne cadeau au même
 *    moment. La clé est relue depuis /cart.js au moment du clic, l'état rendu
 *    dans le DOM pouvant dater de plusieurs minutes (autre onglet, retour
 *    arrière, panier modifié ailleurs).
 *
 * 3. ON PASSE PAR window.fetch, PAS PAR LE FETCH NATIF. On VEUT que le watcher
 *    de free-gift.js voie l'ajout et programme son evaluate() pour poser le
 *    cadeau. Ajouter le cadeau ici créerait une seconde source de vérité sur
 *    son cycle de vie. Le wrapper "Fix 1.1" de cart-drawer.js, lui, ne réagit
 *    qu'à /cart/add ET drawer fermé : sans effet ici, le drawer est ouvert.
 *
 * L'état d'avancement et les messages d'erreur vivent sur <cart-drawer> et non
 * sur le bouton : le bouton est détruit par le premier rafraîchissement venu,
 * <cart-drawer> n'est remplacé par aucun chemin de rendu. Le CSS fait le reste.
 * =============================================================================
 */
(function () {
  'use strict';

  // Ce fichier est chargé depuis snippets/cart-drawer.liquid, rendu une fois par
  // page. Garde défensive au cas où un autre chemin le rechargerait.
  if (window.__vpCartRewardsSwap) return;
  window.__vpCartRewardsSwap = true;

  const JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  const NOTICE_MS = 9000;    // durée d'affichage d'un message d'échec
  const RETRY_MS = 600;      // une seule nouvelle tentative, sur le retrait seulement

  let noticeTimer = null;

  const root = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function setNotice(drawer, value) {
    if (noticeTimer) {
      clearTimeout(noticeTimer);
      noticeTimer = null;
    }
    if (!value) {
      delete drawer.dataset.swapNotice;
      return;
    }
    drawer.dataset.swapNotice = value;
    noticeTimer = setTimeout(() => { delete drawer.dataset.swapNotice; }, NOTICE_MS);
  }

  // /cart.js ne matche ni le watcher de free-gift.js (/cart/add|change|update|clear)
  // ni le wrapper Fix 1.1 (/cart/add) : aucun effet de bord.
  function readCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null));
  }

  // La ligne visée : d'abord par sa clé, puis par sa variante si le panier a
  // bougé. Le repli exclut les lignes à propriétés ou à abonnement, comme le
  // fait Liquid côté rendu : les échanger perdrait ces données en silence.
  function findLine(cart, key, variantId) {
    return (
      cart.items.find((it) => it.key === key) ||
      cart.items.find(
        (it) =>
          String(it.variant_id) === variantId &&
          !(it.properties && it.properties._gift) &&
          Object.keys(it.properties || {}).length === 0 &&
          !it.selling_plan_allocation
      ) ||
      null
    );
  }

  function removeByKey(key) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then((r) => r.ok || r.status === 404)   // 404 : ligne déjà absente, donc succès
      .catch(() => false);
  }

  async function swap(button) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.updateDrawerContent !== 'function') return;

    // Garde principale : elle vit hors du DOM détruit, donc un double clic reste
    // bloqué même si le bouton a été recréé entre-temps.
    if (drawer.dataset.swapping === 'true') return;

    const fromId = String(button.dataset.from || '');
    const toId = Number(button.dataset.to || 0);
    if (!fromId || !toId) return;

    drawer.dataset.swapping = 'true';
    setNotice(drawer, null);

    try {
      const cart = await readCart();
      if (!cart) throw new Error('lecture du panier impossible');

      const line = findLine(cart, button.dataset.lineKey, fromId);
      // Ligne déjà échangée ailleurs : on ne fait rien, le rendu final
      // resynchronise l'affichage sur l'état serveur.
      if (!line) return;

      // Idempotence : si un 100 ml est déjà présent (double soumission, autre
      // onglet), on ne rajoute rien et on se contente du retrait.
      const alreadyThere = cart.items.some((it) => String(it.variant_id) === String(toId));

      if (!alreadyThere) {
        const added = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ id: toId, quantity: line.quantity })
        });

        // 422 = rupture de stock. Aucun retrait n'a eu lieu, le panier est
        // strictement intact. On sort sans toucher à quoi que ce soit.
        if (!added.ok) {
          setNotice(drawer, 'oos');
          return;
        }
      }

      let removed = await removeByKey(line.key);
      if (!removed) {
        await wait(RETRY_MS);
        removed = await removeByKey(line.key);
      }

      // Pas de retour arrière sur l'ajout : le retirer peut échouer aussi et
      // laisserait le pire des états. On expose la situation et on rend la main.
      if (!removed) setNotice(drawer, 'partial');
    } catch (error) {
      console.error('[cart-rewards] échange impossible', error);
      setNotice(drawer, 'partial');
    } finally {
      // Un seul rafraîchissement ici. free-gift.js en déclenchera un second
      // environ 400 ms plus tard, une fois le cadeau posé : c'est déjà le
      // comportement d'un ajout de 100 ml depuis la fiche produit.
      try {
        await drawer.updateDrawerContent();
      } catch (error) {
        console.error('[cart-rewards] rafraîchissement impossible', error);
      }
      drawer.dataset.swapping = 'false';
    }
  }

  // Délégation sur document : survit au remplacement du DOM par n'importe lequel
  // des chemins de rendu, sans entrée dans initAllFeatures() ni reinitFeatures().
  document.addEventListener('click', (event) => {
    const button =
      event.target && event.target.closest
        ? event.target.closest('[data-cart-rewards-swap]')
        : null;
    if (!button) return;
    event.preventDefault();
    swap(button);
  });
})();
